/**
 * Splash screen shown once per browser session on app load.
 *
 * Displays the Dice Frog logo with a scale-in animation. Holds open until
 * both the minimum display time has elapsed AND `isReady` is true (prevents
 * white-flash during auth hydration). Skipped if the user prefers reduced
 * motion or if the session has already shown it.
 *
 * @module SplashScreen
 */

import { useEffect, useState } from 'react';

const SPLASH_STORAGE_KEY = 'splashShown';
const AUTO_DISMISS_MS = 1500;

interface SplashScreenProps {
  /** Called when the splash screen should be removed from the tree */
  onDismiss: () => void;
  /**
   * When true, the splash is allowed to dismiss once the timer fires.
   * Pass `false` to hold the splash open (e.g. while auth is hydrating).
   * Defaults to true.
   */
  isReady?: boolean;
}

/**
 * Full-viewport splash overlay shown once per browser session.
 * Respects `prefers-reduced-motion` by skipping immediately.
 * Holds open until both the minimum display time has elapsed AND isReady is true.
 */
export function SplashScreen({ onDismiss, isReady = true }: SplashScreenProps) {
  const [timerFired, setTimerFired] = useState(false);

  useEffect(() => {
    // Mark as shown so it won't repeat this session
    sessionStorage.setItem(SPLASH_STORAGE_KEY, '1');

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      onDismiss();
      return;
    }

    // Signal that minimum display time has elapsed
    const dismissTimer = setTimeout(() => setTimerFired(true), AUTO_DISMISS_MS);

    return () => {
      clearTimeout(dismissTimer);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Dismiss once both conditions are met: timer elapsed AND app is ready
  useEffect(() => {
    if (timerFired && isReady) {
      onDismiss();
    }
  }, [timerFired, isReady, onDismiss]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background">
      <img
        src="/images/frog/logo-dice-frog.png"
        alt="Dice Frog logo"
        className="w-[480px] h-auto max-w-[85vw] animate-splash-in"
      />
    </div>
  );
}
